// Auto Breeze (Cadence): the AI that places a phone call for its owner.
// These are the shapes of its HTTP API and live feed, shared by the Worker
// (worker/src/cadence/*), the web call screen, and Seamless, which builds the
// Auto Breeze tab on top of them.

import type { ScreenVerdict } from "./screen.js";

/** Where a call is. `finding_number` is looking up who to call (asked by
 * name, not number); `needs_number` could not confirm a number and waits
 * for one (`needsReason` says what it found). `ready` is prepared and waits for "Call now" (or a
 * preview first); `preview` is a practice run where the owner plays the
 * person being called and nothing is kept; `scheduled` waits for its time; `dialing` and `ringing`
 * are before anyone answers; `on_hold` and `talking` are the AI working the
 * call; `needs_you` has rung the owner and waits for them; `with_you` has
 * the owner in the call. The rest are final. */
export type CadenceStatus =
  | "finding_number"
  | "needs_number"
  | "ready"
  | "preview"
  | "scheduled"
  | "dialing"
  | "ringing"
  | "on_hold"
  | "talking"
  | "needs_you"
  | "with_you"
  | "done"
  | "no_answer"
  | "busy"
  | "failed"
  | "canceled";

export const CADENCE_FINAL: readonly CadenceStatus[] = ["done", "no_answer", "busy", "failed", "canceled"];

/** Who is talking in the phone call: the far end, the AI, or the owner. A
 * `note` is something that happened (a key pressed, the owner joining). */
export type CadenceSpeaker = "them" | "breeze" | "adam" | "note";

/** How the far end sounded saying a line, heard from their voice. Absent
 * is neutral (or not checked yet: it arrives a moment after the line, in a
 * `call` message). */
export type CadenceTone = "laughing" | "annoyed" | "rushed" | "confused" | "warm";

export interface CadenceLine {
  /** Milliseconds since the call connected. */
  readonly at: number;
  readonly speaker: CadenceSpeaker;
  readonly text: string;
  /** Only on `them` lines. */
  readonly tone?: CadenceTone;
}

/** `POST /api/cadence/calls`. The short form is `who` and `why`: Breeze
 * finds the number (status `finding_number`), checks it on the
 * organization's own website, and dials. Give `to` as well (a past callee's
 * number from `GET /api/cadence/people`) to skip the lookup. */
export interface CadenceCreateRequest {
  /** Who to call, in plain words ("GEHA", "Dr. Patel's office"). */
  readonly who?: string;
  /** Why, in plain words. What the AI tries to get done. */
  readonly why?: string;
  /** The number to call. US numbers in any common format, or E.164.
   * Absent: looked up from `who`. */
  readonly to?: string;
  /** The same as `who` (the older name). */
  readonly toName?: string;
  /** The same as `why` (the older name). */
  readonly goal?: string;
  /** Anything else it should know or do. */
  readonly instructions?: string;
  /** Facts it may give if asked, and only if asked ("Date of birth: ...").
   * Anything not here it will not say; it rings the owner instead. */
  readonly mayShare?: readonly string[];
  /** What happens once the call gets its answer ("Add the appointment to
   * the calendar"), shown under the call like a draft's steps (Adam,
   * 2026-09-29). Breeze keeps and returns them; the app that made the call
   * runs them. */
  readonly nextSteps?: readonly string[];
  /** The other side will want the owner himself (an insurer, a bank,
   * anything with identity checks): Breeze gets through the menus and the
   * hold, and when a person comes on it says it is getting the owner and
   * rings them, with `questions` ready on the call. If the owner does not
   * pick up, Breeze carries on itself. Absent: true when `who` and `why`
   * read like such a call; false turns it off. */
  readonly handoff?: boolean;
  /** Only for a call to the owner's own Breeze line: the part the line's
   * Breeze plays when it answers ("A pharmacy that will only book with Adam
   * himself"), so a test call has someone real-sounding on the other end. */
  readonly lineRole?: string;
  /** "live": run the call on Gemini Live (one model that hears and speaks,
   * quicker to answer, about 10 to 15 cents for five minutes) while the
   * month's Live spend is under its cap; otherwise, and whenever Live cannot
   * carry on, the usual pipeline. Absent: the pipeline. */
  readonly engine?: "live";
  /** ISO time to place the call. Absent or past: now. */
  readonly at?: string;
  /** Prepare the call without dialing (`ready`), to preview it first or
   * dial it later with `POST /api/cadence/calls/<id>/dial`. */
  readonly hold?: boolean;
  /** Force the "this call is recorded" line on (true) or off (false).
   * Absent: said only when the number is in a state that requires it. */
  readonly recordingNotice?: boolean;
  /** False: keep no audio of this call, so no recording notice is needed
   * (a call to a friend in an all-party state). The transcript is kept. */
  readonly record?: boolean;
  /** An Aura-2 voice name for this call ("andromeda"); absent is the default. */
  readonly voice?: string;
  /** A practice run on the owner's own phone: Breeze calls them from the
   * Breeze line, they play `who`, and when the call would end Breeze asks
   * how it went. The outcome is their feedback. No ring first unless
   * `ringFirst` is true. */
  readonly practice?: boolean;
  /** The language to call in: "en", "es", "fr", "de", "it", "nl", or "ja".
   * Absent is English; Breeze also switches when the other side does. */
  readonly language?: string;
  /** A short name for the call's card; made from the goal when absent. */
  readonly label?: string;
  /** Before dialing, Breeze rings the owner ("Breeze: calling <name>") for up
   * to 12 s. Answering joins the call's room as a listener before the far end
   * picks up; declining, or no answer, dials without them. False skips it. */
  readonly ringFirst?: boolean;
  /** The app joins the call's room as a listener by itself (`POST .../join
   * {listen: true}`), so Breeze sends no ring: it waits up to 10 s for that
   * join, counts the listener in, then dials. With no join in time it dials
   * anyway and a late join is still a listener. `POST .../dial` takes the
   * same flag on a ready call and then answers at once instead of after the
   * pre-dial wait, so the join can follow the reply. Absent: the ring. */
  readonly autoListen?: boolean;
  /** A plain phone call from a room the owner is in right now: no AI. The
   * number (`to`, or a number or saved contact in `who`) is dialed from the
   * owner's caller ID and joins `room` as a participant whose snapshot
   * carries `phoneCall`. `why` is optional. It hangs up when the last person
   * leaves the room, or with `POST .../cancel`. */
  readonly direct?: boolean;
  /** For `direct`: the room, which must be the owner's own. */
  readonly room?: { readonly kind: "personal" | "once"; readonly slug: string };
}

export interface CadenceCall {
  readonly id: string;
  readonly status: CadenceStatus;
  /** E.164. Empty while `finding_number` or `needs_number`. */
  readonly to: string;
  readonly toName: string | null;
  /** Where the number came from when Breeze looked it up: a page on the
   * organization's own site that shows it. Null when the number was given. */
  readonly numberSource: string | null;
  /** When the current hold began (ISO), while `on_hold`. */
  readonly holdSince: string | null;
  /** The owner is in the call's room listening, and the AI is still the
   * one talking. `POST .../mode {mode: "adam"}` takes over. */
  readonly listening: boolean;
  readonly goal: string;
  /** A few words for the call's card ("Nobel Prize bit for Reilly"), made
   * from the goal when the call is created; null until it is ready, and the
   * app shows it instead of the goal. Adam, 2026-09-28: "don't show me that
   * whole why prompt. just a short verison to me". */
  readonly label: string | null;
  /** A plain phone call placed from a room (no AI). */
  readonly direct: boolean;
  /** The call Breeze placed to the owner to ask what it could not settle
   * (a number it could not find or confirm), once it has placed one. */
  readonly questionCall?: string | null;
  /** The language Breeze is speaking on the call ("en", "es", ...). */
  readonly language?: string;
  /** The language the call was made in, before any switch; what Call again
   * repeats. */
  readonly askedLanguage?: string | null;
  readonly instructions: string | null;
  readonly mayShare: readonly string[];
  /** What happens once the call gets its answer; empty when none were given. */
  readonly nextSteps: readonly string[];
  /** A planned handoff (`handoff` on the request): Breeze brings the owner
   * in as soon as a person answers. */
  readonly handoff: boolean;
  /** For a planned handoff: what the owner asks once they have the call,
   * one short question each, drafted from the goal when the call is made.
   * Empty until ready, and on any other call. */
  readonly questions: readonly string[];
  /** The part the line plays, on a test call to the owner's own line. */
  readonly lineRole?: string;
  /** For a call asked to run on Live: what it ran on ("live", "live, then
   * pipeline", or "pipeline" when Live was not used). */
  readonly engine?: string;
  /** On a call bridged into a room: how far behind the phone heard the
   * room, from Twilio's own playback marks. */
  readonly phoneLag?: { readonly medianMs: number; readonly maxMs: number; readonly n: number };
  /** A practice run on the owner's phone, and the voice it used: kept so
   * the call can be placed again as it was (`POST .../again`). */
  readonly practice?: boolean;
  readonly voice?: string;
  /** Times two sides talked over each other, and times Breeze gave way. */
  readonly overlaps?: number;
  readonly breezeYields?: number;
  /** A practice, simulated, or line-test call: nothing it records is filed
   * anywhere as a real call. */
  readonly test?: boolean;
  /** A call Breeze answered to screen it (screen.ts): who the caller said
   * they are, and how it ended once it has. */
  readonly screen?: {
    readonly caller: string | null;
    readonly verdict: ScreenVerdict | null;
    readonly message: string | null;
  };
  /** What the call has cost so far, in US dollars at list prices. */
  readonly spendUsd?: number;
  /** When it was set to dial (ISO). */
  readonly scheduledFor: string | null;
  readonly startedAt: string | null;
  readonly endedAt: string | null;
  /** Why it rang the owner, while it is `needs_you`. */
  readonly needsReason: string | null;
  /** The Breeze room the owner joins to be in the call (a one-off code),
   * once one exists. Open `https://meet.adammirmina.com/j/<room>`. */
  readonly room: string | null;
  /** Whether the AI is talking (`ai`) or quiet and listening (`adam`). */
  readonly mode: "ai" | "adam";
  /** One line: what happened. Set when the call ends. */
  readonly outcome: string | null;
  /** A short paragraph. Set when the call ends. */
  readonly summary: string | null;
  readonly actionItems: readonly string[];
  readonly transcript: readonly CadenceLine[];
  /** Whether the "this call is recorded" line was said. */
  readonly recordingNotice: boolean;
  /** `GET /api/cadence/calls/<id>/recording` serves it (a WAV) when true. */
  readonly hasRecording: boolean;
  /** Where a person comes on in the recording, in ms from its start: after
   * a phone menu, a transfer, or hold. A player opens a second before the
   * first and skips forward to the next. Empty when none was heard. */
  readonly moments?: readonly number[];
  readonly error: string | null;
}

/** A row of `GET /api/cadence/calls`: the call without its transcript. */
export type CadenceCallSummary = Omit<CadenceCall, "transcript">;

/** `POST /api/cadence/calls/<id>/join`: the room to open, and whether the
 * owner's phone was rung for it. Body `{listen: true}` joins to listen:
 * the AI keeps the call, the owner's mic never reaches the phone, and
 * `POST .../mode {mode: "adam"}` is Take over. Without it, joining is
 * taking over (the ring path). A listen join's `url` carries `?listen=1`,
 * so the web room opens with the mic muted and the camera off; its mic
 * button unmutes. */
export interface CadenceJoinResponse {
  readonly room: string;
  readonly url: string;
  readonly rang: boolean;
}

/** `POST /api/cadence/calls/<id>/preview`: a practice run of a call that
 * has not dialed. Join the room and answer as the person being called;
 * Breeze talks to you exactly as it would to them. Its transcript streams
 * on `/api/cadence/calls/<previewId>/live`, and everything about it is
 * deleted when it ends (you leave the room, it hangs up, or
 * `POST /api/cadence/calls/<previewId>/cancel`). */
export interface CadencePreviewResponse {
  readonly previewId: string;
  readonly room: string;
  readonly url: string;
}

/** A row of `GET /api/cadence/people`: someone the owner has called
 * before, newest first, for the "who" suggestions. Create with
 * `{who: name, to: number, why}` to call them again without a lookup. */
/** `GET /api/cadence/people`: saved contacts and everyone called before.
 * A saved contact never called has `calls: 0` and `lastCalledAt: ""`.
 * `POST /api/cadence/people {name, number}` saves one (the same number
 * again renames it). A "who" that matches a saved name, whole or first
 * word, ignoring case, dials that number. */
export interface CadencePerson {
  readonly name: string;
  readonly number: string;
  readonly lastCalledAt: string;
  readonly calls: number;
}

/** The live feed, `GET /api/cadence/calls/<id>/live` (a WebSocket). */
export type CadenceLiveMessage =
  | { readonly type: "call"; readonly call: CadenceCall }
  | { readonly type: "line"; readonly line: CadenceLine }
  | { readonly type: "interim"; readonly speaker: "them" | "adam"; readonly text: string };
